import React, { useEffect, useMemo } from 'react';
import useSWR from 'swr';

import { useRequiredContext } from './common/hooks-util';
import { notEmpty } from './common/util';
import { AppInsightsInstanceContext } from './contexts/app-insights-instance-context';
import { AuthContext } from './contexts/auth-context';
import { useCompany } from './contexts/company-context';
import { UserContext, UserContextProps } from './contexts/user-context';
import { ErrorProvider } from './error-provider';
import { UsersService } from './generated/tellus';
import { isCompanyRole } from './pages/policies';

export const UserProvider: React.FC = ({ children }) => {
  const { isAuthenticated, isComplete } = useRequiredContext(AuthContext);
  const appInsights = useRequiredContext(AppInsightsInstanceContext);

  const { data, error, isLoading, mutate } = useSWR(
    isComplete && isAuthenticated && { _key: 'CurrentUser' },
    UsersService.getCurrentUser
  );

  const { activeCompanyKey, setAvailableCompanyKeys } = useCompany();

  useEffect(() => {
    if (data != null) {
      appInsights.setAuthenticatedUserContext(data.id.toString());
    } else {
      appInsights.clearAuthenticatedUserContext();
    }

    if (data != null) {
      const companies = data?.roles
        .filter((x) => isCompanyRole(x.role))
        .map((x) => x.scope)
        .filter(notEmpty);
      setAvailableCompanyKeys([...new Set(companies)]);
    }
  }, [appInsights, data, setAvailableCompanyKeys]);

  const user = useMemo((): UserContextProps | undefined => {
    if (isLoading || data == null) {
      return;
    }

    return {
      id: data.id.toString(),
      email: data.email,
      phonenumber: data.phoneNumber,
      name: data.name,
      companyKey: activeCompanyKey,
      roles: data?.roles || [],
      updateUser: () => mutate(),
    };
  }, [activeCompanyKey, data, isLoading, mutate]);

  return (
    <ErrorProvider error={error}>
      <UserContext.Provider value={user}>{children}</UserContext.Provider>
    </ErrorProvider>
  );
};
